/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import {
  ApolloClient, InMemoryCache, ApolloProvider, createHttpLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { sha256 } from 'js-sha256';
import PropTypes from 'prop-types';
import React, { useRef, useMemo } from 'react';

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function ApolloWrapper({ token = undefined, children = undefined }) {
  /* –– Resources
   * –––––––––––––––––––––––––––––––– */
  const operationStateDictionary = useRef({});

  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  const updateOperationState = (operationRoot, query) => {
    const isMutation = query.definitions[0].operation === 'mutation';

    if (isMutation) {
      operationStateDictionary.current[operationRoot] ??= { mutationCount: 0 };
      operationStateDictionary.current[operationRoot].mutationCount += 1;
    }
  };

  const getStatefulOperationName = (operationName, operationRoot) => {
    const { mutationCount } = operationStateDictionary.current[operationRoot] || {};
    return mutationCount ? `${operationName}-${mutationCount}` : operationName;
  };

  /* –– Rendering values
   * –––––––––––––––––––––––––––––––– */
  const client = useMemo(() => {
    const httpLink = createHttpLink({ uri: '/gql/v1' });

    const authLink = setContext(({ query, variables, operationName }, { headers }) => {
      let idempotencyKeyHeaders = {};

      // skip Idempotency-Key that ends with NoId
      if (!operationName.endsWith('NoId')) { // no idempotency key
        const operationRoot = operationName.split(/(Create|Fetch|Update|Delete)/)[0];
        updateOperationState(operationRoot, query);
        const statefulOperationName = getStatefulOperationName(operationName, operationRoot);

        idempotencyKeyHeaders = {
          'Idempotency-Key': sha256(JSON.stringify({
            token, query, variables, statefulOperationName,
          })),
        };
      }

      return {
        // get the authentication token from local storage if it exists
        // return the headers to the context so httpLink can read them
        headers: {
          ...headers,
          Authorization: token,
          ...idempotencyKeyHeaders,
        },
      };
    });

    return new ApolloClient({
      link:  authLink.concat(httpLink),
      cache: new InMemoryCache(),
    });
  }, [token]);

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
ApolloWrapper.propTypes = {
  token:    PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default ApolloWrapper;
